import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import HttpClientXml from "../utils/HttpClientXml";
import HomeService from "../Services/HomeService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function ManagePeopleSaying() {
  const [peopleSaying, setPeopleSaying] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemID, setItemID] = useState("");
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState({
    heading: "",
    name: "",
    designation: "",
    image: "",
  });

  // Function to show heading in popup
  const showHeadingPopup = (heading, title = "Testimonial") => {
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto; padding: 10px;">
          <p style="white-space: pre-wrap; word-wrap: break-word;">${
            heading || "No testimonial available"
          }</p>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      width: "600px",
      customClass: {
        popup: "description-popup",
      },
    });
  };

  const columns = [
    {
      name: "S.No.",
      selector: (row, index) => index + 1,
      sortable: true,
    },
    {
      name: "Image",
      cell: (row) => (
        <img
          src={row.image || "/placeholder-image.png"}
          alt="Person"
          width="60"
          height="60"
          style={{
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid #dee2e6",
          }}
        />
      ),
      center: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span style={{ minWidth: "50px" }}>
            {row.name || <span className="text-muted">No name</span>}
          </span>
        </div>
      ),
    },
    {
      name: "Designation",
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span className="me-2" style={{ minWidth: "50px" }}>
            {row.designation || (
              <span className="text-muted">No designation</span>
            )}
          </span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Testimonial",
      cell: (row) => {
        const heading = row.heading || "";
        const shortHeading =
          heading.length > 15 ? `${heading.substring(0, 15)}...` : heading;

        return (
          <div className="d-flex align-items-center">
            <span className="me-2" style={{ minWidth: "50px" }}>
              {shortHeading || (
                <span className="text-muted">No testimonial</span>
              )}
            </span>
            {heading.length > 15 && (
              <button
                type="button"
                className="btn btn-sm p-0"
                onClick={() => showHeadingPopup(heading, "Testimonial")}
                title="View full testimonial"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#007bff",
                  fontSize: "16px",
                }}
              >
                <i className="fa-solid fa-eye"></i>
              </button>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) =>
        new Date(
          row.createdAt || row.createdOn || row.updatedAt
        ).toLocaleDateString(),
      sortable: true,
      center: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <button
            title="Edit"
            onClick={() => handleEdit(row)}
            className="btn btn-sm me-2"
            style={{
              background: "transparent",
              border: "none",
              color: "rgb(133, 218, 79)",
            }}
          >
            <i className="fa-solid fa-pen-to-square fs-5"></i>
          </button>
          <button
            title="Delete"
            onClick={() => handleDelete(row._id)}
            className="btn btn-sm"
            style={{
              background: "transparent",
              border: "none",
              color: "#dc3545",
            }}
          >
            <i className="fa-solid fa-trash-can fs-5"></i>
          </button>
        </div>
      ),
      center: true,
    },
  ];

  // Fetch all people saying items
  const fetchPeopleSaying = async () => {
    setLoading(true);
    try {
      const res = await HomeService.getPeopleSaying();
      console.log("People Saying data:", res.data);
      setPeopleSaying(res.data);
    } catch (error) {
      console.error("Error fetching people saying:", error);
      toast.error("Failed to load testimonials!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPeopleSaying();
  }, []);

  // Handle edit button click
  const handleEdit = (row) => {
    setIsEdit(true);
    setEditData({
      heading: row.heading || "",
      name: row.name || "",
      designation: row.designation || "",
      image: row.image || "",
    });
    setItemID(row._id);
  };

  // Handle delete button click with SweetAlert confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Testimonial?",
      text: "Are you sure you want to delete this testimonial?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const res = await HomeService.deletePeopleSaying(id);
        if (res?.status) {
          Swal.fire("Deleted!", "Testimonial has been deleted.", "success");
          fetchPeopleSaying();
        } else {
          toast.error(res?.message || "Failed to delete testimonial");
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to delete testimonial.", "error");
      }
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    if (!e.target.files[0]) return;

    const data = new FormData();
    data.append("image", e.target.files[0]);
    try {
      const res = await HttpClientXml.fileUplode("image-upload", "POST", data);
      if (res?.image) {
        setEditData({ ...editData, image: res.image });
        toast.success("Image uploaded successfully!");
      }
    } catch (error) {
      toast.error("Failed to upload image.");
      console.error("Error uploading image:", error);
    }
  };

  // Handle form submission for update
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editData.heading.trim()) {
      toast.error("Please enter a testimonial.");
      return;
    }

    if (!editData.name.trim()) {
      toast.error("Please enter a name.");
      return;
    }

    if (!editData.designation.trim()) {
      toast.error("Please enter a designation.");
      return;
    }

    if (!editData.image) {
      toast.error("Please upload an image.");
      return;
    }

    try {
      // Prepare update data
      const data = {
        heading: editData.heading.trim(),
        name: editData.name.trim(),
        designation: editData.designation.trim(),
        image: editData.image,
      };

      console.log("Updating testimonial with data:", data);

      const res = await HomeService.updatePeopleSaying(itemID, data);

      if (res?.status) {
        toast.success("Testimonial updated successfully!");
        setIsEdit(false);
        setEditData({ heading: "", name: "", designation: "", image: "" });
        fetchPeopleSaying();
      } else {
        toast.error(res?.message || "Failed to update testimonial.");
      }
    } catch (error) {
      toast.error("Failed to update testimonial.");
      console.error("Error updating testimonial:", error);
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setIsEdit(false);
    setEditData({ heading: "", name: "", designation: "", image: "" });
  };

  return (
    <>
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          {isEdit ? (
            <section className="chartBox_area">
              <div className="card">
                <div className="card-header">
                  <h4 className="mb-0">Edit Testimonial</h4>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* Image Upload Section */}
                      <div className="col-md-6 mt-3">
                        <label>
                          Image<span className="text-danger">*</span>
                        </label>
                        {editData.image && (
                          <div className="mt-2 mb-2">
                            <img
                              src={editData.image}
                              alt="Person Preview"
                              style={{
                                maxHeight: "150px",
                                maxWidth: "100%",
                                borderRadius: "50%",
                                border: "2px solid #dee2e6",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control"
                          onChange={handleImageUpload}
                          accept="image/*"
                        />
                        <small className="text-muted">
                          Upload person's image (circular preview)
                        </small>
                      </div>

                      {/* Heading/Testimonial Textarea */}
                      <div className="col-md-6 mt-3">
                        <label htmlFor="heading">
                          Testimonial<span className="text-danger">*</span>
                        </label>
                        <textarea
                          id="heading"
                          className="form-control"
                          value={editData?.heading || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              heading: e.target.value,
                            }))
                          }
                          rows="4"
                          placeholder="Enter testimonial or quote..."
                          required
                          style={{ height: "172px" }}
                        ></textarea>
                        <small className="text-muted">
                          Enter what the person said
                        </small>
                      </div>

                      {/* Name Input */}
                      <div className="col-md-6 mt-3">
                        <label htmlFor="name">
                          Name<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          className="form-control"
                          value={editData?.name || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          placeholder="Enter person's name..."
                          required
                        />
                        <small className="text-muted">
                          Enter the name of the person
                        </small>
                      </div>

                      {/* Designation Input */}
                      <div className="col-md-6 mt-3">
                        <label htmlFor="designation">
                          Designation<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="designation"
                          className="form-control"
                          value={editData?.designation || ""}
                          onChange={(e) =>
                            setEditData((prev) => ({
                              ...prev,
                              designation: e.target.value,
                            }))
                          }
                          placeholder="Enter designation..."
                          required
                        />
                        <small className="text-muted">
                          Enter the designation or profession
                        </small>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-top">
                      <button
                        type="submit"
                        className="btn btn-primary me-3"
                        style={{
                          background: "rgb(133, 218, 79)",
                          border: "none",
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa-solid fa-floppy-disk me-2"></i>
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="btn btn-secondary"
                        style={{
                          padding: "10px 25px",
                          fontWeight: "500",
                        }}
                      >
                        <i className="fa-solid fa-times me-2"></i>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </section>
          ) : (
            <>
              <div className="mt-5">
                <div style={headLineStyle} className="page-headline">
                  Manage People Saying
                </div>
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading testimonials...</p>
                  </div>
                ) : peopleSaying.length > 0 ? (
                  <div className="card">
                    <div className="card-body">
                      <DataTable
                        columns={columns}
                        data={peopleSaying}
                        pagination
                        responsive
                        highlightOnHover
                        striped
                        paginationPerPage={10}
                        paginationRowsPerPageOptions={[10, 20, 30, 50]}
                        noDataComponent={
                          <div className="text-center py-4">
                            <i className="fa-solid fa-comment fa-2x text-muted mb-3"></i>
                            <p>No testimonials found</p>
                          </div>
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <i className="fa-solid fa-comment fa-3x text-muted mb-3"></i>
                      <h5>No Testimonials Available</h5>
                      <p className="text-muted">
                        There are no testimonials to display at the moment.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add CSS for the popup */}
      <style>
        {`
          .description-popup .swal2-popup {
            max-width: 600px;
          }
          .description-popup .swal2-html-container {
            overflow: hidden;
          }
        `}
      </style>
    </>
  );
}

export default ManagePeopleSaying;
